"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { PhoneForwarded, Undo2 } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import { useToast } from "@/app/_module/hooks/use-toast";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";

/**
 * LIVE TRANSFER — inline dropdown panel (2026-07-23).
 *
 * Shared by the session dialers (multi-line + power compact view) so the
 * target list expands IN PLACE under the "Transfer Call" button, exactly like
 * the manual widget's inline panel — no floating popup.
 *
 * Rules:
 *  - Available → clickable
 *  - On a call / Offline → DISABLED (an offline owner's CRM can't ring;
 *    letting agents click it just burned the 35s timeout)
 *  - While ringing: inline banner + Cancel; auto-clears on accept/decline.
 * The agent's device drop on 'accepted' is handled centrally by
 * TwilioDeviceContext — this panel only manages the picker + ring state.
 */
type TransferTarget = {
  userId: string;
  name: string;
  agencyName: string;
  depth: number;
  availability: "available" | "busy" | "offline";
  reachableVia?: "crm" | "cell" | "both";
};

export function InlineTransferPanel({
  callSid,
  leadId,
  leadName,
  onClose,
}: {
  callSid: string;
  leadId?: string;
  leadName?: string;
  onClose?: () => void;
}) {
  const { toast } = useToast();
  const [targets, setTargets] = useState<TransferTarget[]>([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<{
    targetName: string;
    conferenceSid: string;
    participantCallSid: string;
    phoneParticipantCallSid?: string;
    callSid: string;
  } | null>(null);
  const pendingRef = useRef(pending);
  useEffect(() => {
    pendingRef.current = pending;
  }, [pending]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<any>({ url: getApiUrl("/communication/transfer/targets") + `?callSid=${encodeURIComponent(callSid)}`, auth: true });
        const data = (res as any)?.data?.data ?? (res as any)?.data ?? [];
        if (!cancelled) setTargets(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setTargets([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [callSid]);

  const cancelTransfer = useCallback(() => {
    const pt = pendingRef.current;
    setPending(null);
    if (!pt) return;
    api
      .post({
        url: getApiUrl("/communication/transfer/resolve"),
        body: { action: "cancel", callSid: pt.callSid, conferenceSid: pt.conferenceSid, participantCallSid: pt.participantCallSid, phoneParticipantCallSid: pt.phoneParticipantCallSid },
        auth: true,
      })
      .catch(() => {});
  }, []);

  // Accepted → TwilioDeviceContext drops the agent leg; we just clean up.
  // Declined → inform + keep the call.
  useEffect(() => {
    const onStatus = (e: any) => {
      const d = e?.detail || {};
      const pt = pendingRef.current;
      if (!pt || d.callSid !== pt.callSid) return;
      if (d.status === "accepted") {
        setPending(null);
        toast({ title: `Transferred to ${pt.targetName}`, description: "You've been disconnected — the call continues." });
        onClose?.();
      } else if (d.status === "declined") {
        setPending(null);
        toast({ title: `${pt.targetName} declined the transfer`, description: "You're still on the call.", variant: "destructive" });
      }
    };
    window.addEventListener("transfer:status", onStatus);
    const onJoined = (e: any) => {
      const pt = pendingRef.current;
      const sid = e?.detail?.participantSid;
      if (!pt || (sid !== pt.participantCallSid && sid !== pt.phoneParticipantCallSid)) return;
      // Cell won the ring race (call-forwarding) — record accept + stop the browser leg.
      if (sid === pt.phoneParticipantCallSid && sid !== pt.participantCallSid) {
        api.post({
          url: getApiUrl("/communication/transfer/resolve"),
          body: {
            action: "accepted", callSid: pt.callSid, targetName: pt.targetName,
            conferenceSid: pt.conferenceSid,
            cancelLegSid: pt.participantCallSid !== pt.phoneParticipantCallSid ? pt.participantCallSid : undefined,
          },
          auth: true,
        }).catch(() => {});
      }
      window.dispatchEvent(new CustomEvent("transfer:status", { detail: { status: "accepted", callSid: pt.callSid, targetName: pt.targetName } }));
    };
    window.addEventListener("transfer:participant-joined", onJoined);
    return () => {
      window.removeEventListener("transfer:status", onStatus);
      window.removeEventListener("transfer:participant-joined", onJoined);
    };
  }, [toast, onClose]);

  // Ring timeout safety net (35s = 30s leg timeout + grace).
  useEffect(() => {
    if (!pending) return;
    const t = setTimeout(() => {
      if (pendingRef.current) {
        toast({ title: `${pendingRef.current.targetName} didn't answer`, description: "You're still on the call.", variant: "destructive" });
        cancelTransfer();
      }
    }, 35000);
    return () => clearTimeout(t);
  }, [pending, cancelTransfer, toast]);

  const initiate = useCallback(
    async (t: TransferTarget) => {
      if (t.availability !== "available") return; // busy AND offline blocked
      try {
        const res = await api.post<any>({
          url: getApiUrl("/communication/transfer/initiate"),
          body: { callSid, targetUserId: t.userId, leadId, leadName },
          auth: true,
        });
        const d = (res as any)?.data?.data ?? (res as any)?.data;
        if (d?.participantCallSid) {
          setPending({ targetName: t.name, conferenceSid: d.conferenceSid, participantCallSid: d.participantCallSid, phoneParticipantCallSid: d.phoneParticipantCallSid, callSid });
        } else {
          toast({ title: (res as any)?.data?.message || "Couldn't start transfer", variant: "destructive" });
        }
      } catch (e: any) {
        toast({ title: e?.message || "Couldn't start transfer", variant: "destructive" });
      }
    },
    [callSid, leadId, leadName, toast],
  );

  if (pending) {
    return (
      <div className="p-2 border border-emerald-300 rounded-lg bg-emerald-50 flex items-start justify-between gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <span className="h-2 w-2 mt-1 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
          <span className="text-xs text-emerald-900 leading-snug">Ringing {pending.targetName}…</span>
        </div>
        <Button size="sm" variant="ghost" className="h-6 text-xs text-red-600 hover:text-red-700 flex-shrink-0" onClick={cancelTransfer}>
          Cancel
        </Button>
      </div>
    );
  }

  return (
    <div className="p-2 border border-emerald-200 rounded-lg bg-emerald-50/60">
      <div className="flex items-center gap-2 mb-1.5">
        <PhoneForwarded className="h-3.5 w-3.5 text-emerald-600" />
        <span className="text-xs font-medium text-emerald-900">Transfer to</span>
      </div>
      {loading ? (
        <div className="py-2 text-center text-xs text-muted-foreground">Loading uplines…</div>
      ) : targets.length === 0 ? (
        <div className="py-2 text-center text-xs text-muted-foreground">No agency owners found in your upline.</div>
      ) : (
        <div className="space-y-1 max-h-40 overflow-y-auto">
          {targets.map((t) => (
            <button
              key={t.userId}
              type="button"
              disabled={t.availability !== "available"}
              onClick={() => initiate(t)}
              className={`w-full flex items-center justify-between p-1.5 rounded border text-left transition-colors ${
                t.availability !== "available"
                  ? "bg-gray-50 border-gray-200 opacity-60 cursor-not-allowed"
                  : "bg-white border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50"
              }`}
            >
              <div className="min-w-0">
                <div className="text-xs font-medium truncate">{t.name}</div>
                <div className="text-[10px] text-muted-foreground truncate flex items-center gap-1">
                  {t.depth < 0 ? (
                    <>
                      <Undo2 className="h-2.5 w-2.5 flex-shrink-0" />
                      <span>send the call back</span>
                    </>
                  ) : (
                    <span>{t.agencyName}{t.depth === 0 ? " · your agency" : ""}</span>
                  )}
                </div>
              </div>
              <span
                className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                  t.availability === "available"
                    ? (t.reachableVia === "cell" ? "bg-sky-100 text-sky-700" : "bg-green-100 text-green-700")
                    : t.availability === "busy"
                      ? "bg-red-100 text-red-600"
                      : "bg-gray-100 text-gray-500"
                }`}
              >
                {t.availability === "available" ? (t.reachableVia === "cell" ? "Cell" : "Available") : t.availability === "busy" ? "On a call" : "Offline"}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default InlineTransferPanel;
